import type { OidcTokenInfo } from "@/lib/authSession";
import type { RefreshOidcAccessTokenResult } from "@/lib/authSessionErrors";
import {
  invalidateRefreshTokenSession,
  recordOidcRefreshSuccess,
} from "@/lib/authSession";
import { getOidcConfig } from "@/config/auth";

let refreshPromise: Promise<RefreshOidcAccessTokenResult> | null = null;

const calculateExpiresAt = (expiresIn?: unknown) => {
  if (typeof expiresIn === "number") {
    return Date.now() + expiresIn * 1000;
  }

  if (typeof expiresIn === "string") {
    const parsed = Number.parseInt(expiresIn, 10);
    if (!Number.isNaN(parsed)) {
      return Date.now() + parsed * 1000;
    }
  }

  return undefined;
};

export const refreshOidcAccessToken =
  async (): Promise<RefreshOidcAccessTokenResult> => {
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        const oidcConfig = getOidcConfig();
        const tokenEndpoint =
          oidcConfig.metadata?.token_endpoint ||
          `${import.meta.env.VITE_OIDC_AUTHORITY}/oauth/token`;

        const formData = new URLSearchParams();
        formData.append("grant_type", "refresh_token");
        formData.append("client_id", import.meta.env.VITE_OIDC_CLIENT_ID);

        const response = await fetch(tokenEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData,
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          const tokenInfo: OidcTokenInfo = {
            token: data.access_token,
            expiresAt: calculateExpiresAt(data.expires_in),
          };

          recordOidcRefreshSuccess(tokenInfo);
          return { tokenInfo, status: "success" };
        }

        if (response.status === 401 || response.status === 400) {
          invalidateRefreshTokenSession();
          console.log("❌ Refresh token is invalid or expired");
          return { tokenInfo: null, status: "invalid_refresh_token" };
        }

        const errorData = await response.json().catch(() => ({}));
        console.log("Refresh failed:", errorData);
        return { tokenInfo: null, status: "http_error" };
      } catch (error) {
        console.error("Token refresh failed:", error);
        return { tokenInfo: null, status: "network_error" };
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };
